import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Building2, Mail, Home } from "lucide-react";
import { Link } from "wouter";
import { api } from "@/lib/api";
import type { Project, ContactMessage } from "@shared/schema";

export function AdminDashboard() {
  const { t } = useTranslation();

  const projectsQ = useQuery({
    queryKey: ["admin", "projects"],
    queryFn: () => api<Project[]>("/api/admin/projects"),
  });

  const messagesQ = useQuery({
    queryKey: ["admin", "messages"],
    queryFn: () => api<ContactMessage[]>("/api/admin/messages"),
  });

  const unread = messagesQ.data?.filter((m) => !m.isRead).length ?? 0;
  const active = projectsQ.data?.length ?? 0;

  return (
    <div>
      <h1 className="text-2xl font-bold">{t("admin.dashboard.title")}</h1>

      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        <StatCard
          label={t("admin.dashboard.newMessages")}
          value={unread}
          href="/messages"
          icon={Mail}
        />
        <StatCard
          label={t("admin.dashboard.activeProjects")}
          value={active}
          href="/projects"
          icon={Building2}
        />
        <StatCard
          label="Megnyitás"
          value=""
          href="/"
          icon={Home}
          external
        />
      </div>

      <div className="mt-10 card p-6">
        <h2 className="font-semibold text-sm uppercase tracking-wider text-brand-500">
          Legutóbbi üzenetek
        </h2>
        {messagesQ.data && messagesQ.data.length > 0 ? (
          <ul className="mt-4 divide-y divide-brand-100">
            {messagesQ.data.slice(0, 5).map((m) => {
              const name = (m.payload?.name as string) || "—";
              return (
                <li key={m.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">
                      {name}{" "}
                      {!m.isRead && (
                        <span className="ml-2 text-xs bg-accent-100 text-accent-700 px-1.5 py-0.5 rounded">
                          új
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-brand-500">
                      {new Date(m.createdAt).toLocaleString("hu-HU")}
                    </div>
                  </div>
                  <Link
                    href="/messages"
                    className="text-xs text-brand-600 hover:text-brand-900"
                  >
                    Részletek →
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-brand-500">
            Még nem érkezett üzenet.
          </p>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  href,
  icon: Icon,
  external,
}: {
  label: string;
  value: number | string;
  href: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  external?: boolean;
}) {
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <Icon size={22} className="text-brand-400" />
        {value !== "" && (
          <div className="text-3xl font-bold text-brand-900">{value}</div>
        )}
      </div>
      <div className="mt-4 text-sm font-medium text-brand-700">{label}</div>
    </>
  );
  const cls =
    "card p-5 hover:bg-brand-50 transition-colors block";
  return external ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
      {inner}
    </a>
  ) : (
    <Link href={href} className={cls}>
      {inner}
    </Link>
  );
}
